// src/components/LoanFilesTable/hooks/useColumnDefs.js
import { EditOutlined } from "@mui/icons-material";
import { Card, Divider } from "@mui/material";
import { amber, deepPurple, green, orange, red } from "@mui/material/colors";
import isBoolean from "lodash/isBoolean";
import isNil from "lodash/isNil";
import isNumber from "lodash/isNumber";
import isString from "lodash/isString";
import toNumber from "lodash/toNumber";
import { useMemo } from "react";
import CellRendererLoanFileAssociations from "../../../../../../components/agGrid/cellRenderer/CellRendererLoanFileAssociations";
import CellRendererLoanFileStatus from "../../../../../../components/agGrid/cellRenderer/CellRendererLoanFileStatus";
import CellRendererUserByEmail from "../../../../../../components/agGrid/cellRenderer/CellRendererUserByEmail";
import Flx from "../../../../../../components/layout/Flx";
import Txt from "../../../../../../components/typography/Txt";

import { LOAN_FILES_TABLE_LAYOUT_TYPES } from "../../../../../../constants/enums/LOAN_FILES_TABLE_LAYOUT_TYPES";
import { selectOptionsDocumentStatus } from "../../../../../../constants/selectOptions/selectOptionsDocumentStatus";
import CellRendererLoanFileActions from "../CellRendererLoanFileActions";

/**
 * Builds the array of column definitions. Depends on `groupByCategory` and `editing`.
 */
export const useColumnDefs = ({
  // groupByCategory,
  // groupByStatus,
  layoutMode,
  editing,
  sortFilter,
  freezePanes,
  hideDocGroupColumn = false,
  hideDocTypeColumn = false,
  fileDocGroups,
  getDocTypesByGroup,
}) => {
  return useMemo(
    () => [
      {
        width: 155,
        maxWidth: 155,
        flex: 0,
        headerName: "Actions",
        resizable: false,
        cellStyle: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          paddingLeft: "0px",
          borderLeft: "none !important",
        },
        pinned: freezePanes ? "left" : null,
        cellRenderer: (params) => {
          if (params.node.group) {
            return params.value;
          }
          return <CellRendererLoanFileActions params={params} />;
        },
      },

      {
        headerName: "Filename",
        field: "file_display_name",
        cellEditor: "agTextCellEditor",
        editable: editing,
        minWidth: 250,
        pinned: freezePanes ? "left" : null,

        tooltipComponent: () => <FileTooltip editing={editing} />,
        tooltipValueGetter: (params) =>
          editing
            ? "Edit File Name"
            : "Left click to preview file  |  Right click for more options",
        sort:
          sortFilter?.colId === "file_display_name" ? sortFilter?.sort : null,
      },
      {
        headerName: "Status",
        field: "documentStatus",
        rowGroup: layoutMode === LOAN_FILES_TABLE_LAYOUT_TYPES.STATUS,
        pinned: freezePanes ? "left" : null,
        editable: editing,
        cellEditor: "agRichSelectCellEditor",
        sort: sortFilter?.colId === "documentStatus" ? sortFilter?.sort : null,
        valueGetter: (params) => {
          const value = params.data?.documentStatus;
          return value === undefined || value === null || value === ""
            ? "Uncategorized"
            : value;
        },
        minWidth: 160,
        width: 160,
        flex: 0,
        comparator: (valueA, valueB) => {
          const statusOrder = [
            "New Upload",
            "Pending Review",
            "Processor Reviewed",
            "Incomplete",
            "Rejected",
            "Accepted",
          ];
          const indexA = statusOrder.indexOf(valueA);
          const indexB = statusOrder.indexOf(valueB);
          const finalIndexA = indexA === -1 ? statusOrder.length : indexA;
          const finalIndexB = indexB === -1 ? statusOrder.length : indexB;
          return finalIndexA - finalIndexB;
        },
        cellEditorParams: {
          values: selectOptionsDocumentStatus || [],
          allowTyping: true,
          filterList: true,
          highlightMatch: true,
          valueListMaxHeight: 220,
          valueListWidth: 620,
        },
        cellClass: "ag-cell-vertical-center",
        cellStyle: (params) => {
          const status = params?.data?.documentStatus;
          const baseStyles = {
            color: "inherit",
            background: null,
            borderLeft: null,
          };
          if (
            status === "Initial Reviewed" ||
            status === "Legal Reviewed" ||
            status === "Pending Review"
          ) {
            baseStyles.background = amber[50];
            baseStyles.borderLeft = `2px solid ${orange[100]}`;
          }
          if (status === "Processor Reviewed") {
            baseStyles.background = deepPurple[50];
            baseStyles.borderLeft = `2px solid ${deepPurple[100]}`;
          }
          if (
            status === "Expired" ||
            status === "Rejected" ||
            status === "Incomplete"
          ) {
            baseStyles.background = red[50];
            baseStyles.color = red[900];
            baseStyles.borderLeft = `2px solid ${red[100]}`;
          }
          if (status === "Accepted") {
            baseStyles.color = green[900];
            baseStyles.background = green[50];
            baseStyles.borderLeft = `2px solid ${green[100]}`;
          }
          return baseStyles;
        },
        autoHeight: true,
        cellRenderer: (params) => (
          <CellRendererLoanFileStatus params={params} editing={editing} />
        ),
      },
      {
        headerName: "Category",
        field: "docGroup",
        rowGroup:
          layoutMode === LOAN_FILES_TABLE_LAYOUT_TYPES.CATEGORY ||
          layoutMode === LOAN_FILES_TABLE_LAYOUT_TYPES.CATEGORY_AND_TYPE,
        // rowGroup: groupByCategory,
        showRowGroup: "docGroup",
        // sort: "asc",
        sort: isNil(sortFilter)
          ? "asc"
          : sortFilter?.colId === "docGroup"
            ? sortFilter?.sort
            : null,
        minWidth: 220,
        flex: 0,
        editable: editing,
        hide: hideDocGroupColumn,
        cellEditor: "agRichSelectCellEditor",
        valueGetter: (params) => {
          const value = params.data?.docGroup;
          return value === undefined || value === null || value === ""
            ? "Uncategorized"
            : value;
        },
        cellEditorParams: {
          values: fileDocGroups || [],
          allowTyping: true,
          filterList: true,
          highlightMatch: true,
          valueListMaxHeight: 220,
          valueListWidth: 620,
        },
      },
      {
        headerName: "Document Type",
        field: "docType",
        // sort: "asc",
        sort: isNil(sortFilter)
          ? "asc"
          : sortFilter?.colId === "docGroup"
            ? sortFilter?.sort
            : null,
        rowGroup:
          layoutMode === LOAN_FILES_TABLE_LAYOUT_TYPES.TYPE ||
          layoutMode === LOAN_FILES_TABLE_LAYOUT_TYPES.CATEGORY_AND_TYPE,
        showRowGroup: "docType",
        editable: editing,
        minWidth: 240,
        flex: 0,
        hide: hideDocTypeColumn,
        cellEditor: "agRichSelectCellEditor",
        cellEditorParams: (params) => {
          return {
            values: getDocTypesByGroup(params.data.docGroup),
            allowTyping: true,
            filterList: true,
            highlightMatch: true,
            valueListMaxHeight: 220,
            valueListWidth: 620,
          };
        },
      },
      {
        headerName: "Associations",
        cellRenderer: CellRendererLoanFileAssociations,
        // field: "docType",
        // // sort: "asc",
        // sort: isNil(sortFilter)
        //   ? "asc"
        //   : sortFilter?.colId === "docGroup"
        //     ? sortFilter?.sort
        //     : null,
        // rowGroup:
        //   layoutMode === LOAN_FILES_TABLE_LAYOUT_TYPES.TYPE ||
        //   layoutMode === LOAN_FILES_TABLE_LAYOUT_TYPES.CATEGORY_AND_TYPE,
        // showRowGroup: "docType",
        // editable: editing,
        // minWidth: 240,
        // flex: 0,
        // hide: hideDocTypeColumn,
        // cellEditor: "agRichSelectCellEditor",
        // cellEditorParams: (params) => {
        //   return {
        //     values: getDocTypesByGroup(params.data.docGroup),
        //     allowTyping: true,
        //     filterList: true,
        //     highlightMatch: true,
        //     valueListMaxHeight: 220,
        //     valueListWidth: 620,
        //   };
        // },
      },
      {
        field: "isHidden",
        headerName: "Is Hidden",
        sortable: true,
        hide: !editing,
        filter: true,
        resizable: false,
        suppressHeaderFilterButton: true,
        suppressHeaderMenuButton: true,
        wrapHeaderText: true,
        autoHeaderHeight: true,
        minWidth: 100,
        width: 100,
        flex: 0,
        editable: editing,
        cellRenderer: "agCheckboxCellRenderer",
        cellEditor: "agCheckboxCellEditor",
        valueGetter: (params) => {
          const name = "isHidden";
          if (isBoolean(params?.data?.[name])) {
            return params?.data?.[name];
          }
          if (isString(params?.data?.[name])) {
            if (
              params?.data?.[name] === "true" ||
              params?.data?.[name] === "True"
            ) {
              return true;
            }
            if (
              params?.data?.[name] === "false" ||
              params?.data?.[name] === "False"
            ) {
              return false;
            }
          }

          return false;
        },
      },
      {
        headerName: "Upload Date",
        field: "uploadDate",
        type: "dateTime",
        width: 160,
        sort: sortFilter?.colId === "uploadDate" ? sortFilter?.sort : null,
        hide: editing,
        wrapHeaderText: true,
        autoHeaderHeight: true,
        suppressHeaderFilterButton: true,
        suppressHeaderMenuButton: true,
      },
      {
        headerName: "Uploaded By",
        field: "uploadedBy",
        width: 160,
        hide: editing,
        wrapHeaderText: true,
        autoHeaderHeight: true,
        suppressHeaderFilterButton: true,
        suppressHeaderMenuButton: true,
        // tooltipValueGetter: (params) => `Year: ${params.value}`,
        tooltipValueGetter: (params) => params.value,
        cellRenderer: CellRendererUserByEmail,
      },
      {
        headerName: "Report Effective Start Date",
        field: "reportEffectiveDate",
        type: "date",
        width: 140,
        editable: editing,
        wrapHeaderText: true,
        autoHeaderHeight: true,
        suppressHeaderFilterButton: true,
        suppressHeaderMenuButton: true,
      },

      {
        headerName: "Report Guideline Expiration Days",
        field: "reportGuidelineExpirationDays",
        type: "number",
        editable: editing,
        suppressHeaderFilterButton: true,
        suppressHeaderMenuButton: true,
        wrapHeaderText: true,
        autoHeaderHeight: true,
        width: 140,
        valueFormatter: (params) => {
          const value = params.value;

          if (isNil(value)) {
            return; // Default to 0 if the value is not a number
          }
          if (isNumber(value)) {
            return value; // Default to 0 if the value is not a number
          }
          if (isString(value)) {
            const convertedValue = toNumber(value);
            if (isNumber(convertedValue)) {
              return convertedValue;
            }
          }
          return;
        },
      },
      {
        headerName: "Last Updated",
        field: "modifiedDate",
        width: 160,

        type: "dateTime",
        wrapHeaderText: true,
        autoHeaderHeight: true,
        suppressHeaderFilterButton: true,
        suppressHeaderMenuButton: true,
        // tooltipValueGetter: (params) => `Year: ${params.value}`,
        tooltipValueGetter: (params) => params.value,
      },
      {
        headerName: "Last Updated By",
        field: "updatedBy",
        width: 160,

        wrapHeaderText: true,
        autoHeaderHeight: true,
        suppressHeaderFilterButton: true,
        suppressHeaderMenuButton: true,
        // tooltipValueGetter: (params) => `Year: ${params.value}`,
        tooltipValueGetter: (params) => params.value,
        cellRenderer: CellRendererUserByEmail,
      },
      {
        field: "excludeFromExport",
        headerName: "Exclude File From Export",
        sortable: true,
        filter: true,
        resizable: true,
        suppressHeaderFilterButton: true,
        suppressHeaderMenuButton: true,
        wrapHeaderText: true,
        autoHeaderHeight: true,
        width: 140,
        editable: editing,
        cellRenderer: "agCheckboxCellRenderer",
        cellEditor: "agCheckboxCellEditor",
        valueGetter: (params) => {
          if (isBoolean(params?.data?.excludeFromExport)) {
            return params?.data?.excludeFromExport;
          }
          if (isString(params?.data?.excludeFromExport)) {
            if (
              params?.data?.excludeFromExport === "true" ||
              params?.data?.excludeFromExport === "True"
            ) {
              return true;
            }
            if (
              params?.data?.excludeFromExport === "false" ||
              params?.data?.excludeFromExport === "False"
            ) {
              return false;
            }
          }

          return false;
        },
      },
      {
        field: "approvedForThirdParty",
        headerName: "File Approved For Third Party",
        sortable: true,
        filter: true,
        resizable: true,
        suppressHeaderFilterButton: true,
        suppressHeaderMenuButton: true,
        wrapHeaderText: true,
        autoHeaderHeight: true,
        width: 140,
        cellRenderer: "agCheckboxCellRenderer",
        cellEditor: "agCheckboxCellEditor",

        editable: editing,
        valueGetter: (params) => {
          if (isBoolean(params?.data?.approvedForThirdParty)) {
            return params?.data?.approvedForThirdParty;
          }
          if (isString(params?.data?.approvedForThirdParty)) {
            if (
              params?.data?.approvedForThirdParty === "true" ||
              params?.data?.approvedForThirdParty === "True"
            ) {
              return true;
            }
            if (
              params?.data?.approvedForThirdParty === "false" ||
              params?.data?.approvedForThirdParty === "False"
            ) {
              return false;
            }
          }

          return false;
        },
      },
    ],
    [
      editing,
      sortFilter,
      layoutMode,
      freezePanes,
      hideDocGroupColumn,
      hideDocTypeColumn,
    ],
  );
};

const FileTooltip = ({ editing }) => {
  if (editing) {
    return (
      <Card sx={{ p: 1 }}>
        <Flx ac gap={0.5}>
          <EditOutlined className="thin-9" sx={{ fontSize: "14px" }} />
          <Txt sx={{ fontSize: "12px" }}>Click to Edit</Txt>
        </Flx>
      </Card>
    );
  }

  return (
    <Card sx={{ p: 1 }}>
      <Flx gap={1.5}>
        <Flx ac gap={0.4}>
          <LeftClickIcon />
          <Txt sx={{ fontSize: "12px" }}>Left Click to Preview</Txt>
        </Flx>
        <Divider flexItem orientation="vertical" />
        <Flx ac gap={0.4}>
          <RightClickIcon />
          <Txt sx={{ fontSize: "12px" }}>Right Click to Edit</Txt>
        </Flx>
      </Flx>
    </Card>
  );
};

// Alternative: As React components for more flexibility
const LeftClickIcon = ({ className = "", ...props }) => (
  <svg
    className={`mouse-click-svg mouse-click-left ${className}`}
    xmlns="http://www.w3.org/2000/svg"
    fill="currentColor"
    viewBox="0 0 450.47 450.47"
    width="1em"
    height="1em"
    {...props}
  >
    <g>
      <path d="M237.298,99.533c-0.301-0.34-0.562-0.677-0.916-0.999c-3.937-3.535-4.043-2.491,0.266-6.463   c3.192-2.929,7.063-5.222,10.574-7.755c9.286-6.711,15.398-15.699,19.529-26.356C276.02,34.033,248.707,17.503,235,3.344   c-9.904-10.247-25.496,5.382-15.604,15.604c7.643,7.912,17.489,14.328,24.14,23.123c7.453,9.848-3.901,20.712-11.68,26.194   c-12.026,8.473-22.423,19.727-20.02,31.794c-53.971,5.042-103.87,34.623-103.87,86.767V333.2c0,64.664,52.603,117.27,117.27,117.27   c64.663,0,117.27-52.605,117.27-117.27V186.817C342.51,129.888,289.543,102.317,237.298,99.533z M130.044,186.817   c0-38.707,42.017-61.117,85.535-64.841v135.005c-39.697-1.998-71.928-11.042-85.535-15.457V186.817z M320.433,333.194   c0,52.5-42.705,95.199-95.192,95.199c-52.488,0-95.196-42.699-95.196-95.199V264.73c19.713,5.958,56.817,14.995,100.676,14.995   c28.088,0,58.93-3.759,89.713-14.352V333.194z M320.433,241.896c-27.916,10.675-56.424,14.849-82.78,15.415v-135.66   c42.569,2.553,82.78,22.969,82.78,65.175V241.896z M206.072,133.429v111.973c-17.153,3.027-67.583-11.094-67.583-11.094   C131.049,155.812,160.429,142.005,206.072,133.429z" />
    </g>
  </svg>
);

const RightClickIcon = ({ className = "", ...props }) => (
  <svg
    className={`mouse-click-svg mouse-click-right ${className}`}
    xmlns="http://www.w3.org/2000/svg"
    fill="currentColor"
    viewBox="0 0 417.031 417.031"
    width="1em"
    height="1em"
    {...props}
  >
    <g>
      <path d="M219.683,92.146c-0.279-0.315-0.52-0.627-0.849-0.925c-3.644-3.272-3.742-2.306,0.247-5.983   c2.955-2.712,6.541-4.834,9.79-7.18c8.596-6.213,14.254-14.534,18.079-24.399c8.582-22.15-16.706-37.453-29.396-50.562   c-9.168-9.485-23.603,4.982-14.444,14.447c7.076,7.325,16.19,13.264,22.349,21.407c6.897,9.116-3.613,19.174-10.814,24.249   c-11.133,7.844-20.757,18.262-18.533,29.434c-49.964,4.668-96.16,32.052-96.16,80.327v135.51   c0,59.862,48.698,108.562,108.564,108.562c59.863,0,108.566-48.7,108.566-108.562V172.95   C317.085,120.247,268.05,94.723,219.683,92.146z M120.391,172.95c0-35.833,38.898-56.581,79.186-60.027v124.982   c-36.751-1.85-66.589-10.222-79.186-14.309V172.95z M296.648,308.461c0,48.604-39.537,88.133-88.129,88.133   c-48.59,0-88.128-39.529-88.128-88.133V245.08c18.249,5.516,52.6,13.882,93.202,13.882c26.003,0,54.556-3.479,83.056-13.286   V308.461z M296.648,223.94c-25.844,9.883-52.237,13.746-76.635,14.271v-125.59c39.407,2.363,76.635,21.264,76.635,60.337V223.94z    M289.735,216.203c0,0-46.688,13.073-62.567,10.271V122.813C269.429,130.753,296.625,143.533,289.735,216.203z" />
    </g>
  </svg>
);
